import helveticaNeueRegularUrl from '../assets/fonts/HelveticaNeue-01.woff2?url'
import helveticaNeueBoldUrl from '../assets/fonts/HelveticaNeue-Bold-02.woff2?url'

export const GLOBAL_HEADER_STYLES = `
        @font-face {
            font-family: 'Helvetica Neue';
            src: url('${helveticaNeueRegularUrl}') format('woff2');
            font-weight: 400;
            font-style: normal;
            font-display: swap;
        }

        @font-face {
            font-family: 'Helvetica Neue';
            src: url('${helveticaNeueBoldUrl}') format('woff2');
            font-weight: 700;
            font-style: normal;
            font-display: swap;
        }

        *, *::before, *::after {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }

        :host {
            /* CSS custom properties for theming */
            --gh-bg: #ffffff;
            --gh-border: #e5e7eb;
            --gh-text: #232529;
            --gh-text-muted: #555860;
            --gh-text-hover: #000000;
            --gh-accent: #f5a623; /* YPO Gold */
            --gh-active-bar: #2f6bff;
            --gh-active-text: #2f6bff;
            --gh-height: 72px;
            --gh-font: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            --gh-font-size: 14px;
            --gh-transition: 0.15s ease;
            --gh-radius: 4px;
            --gh-z: 100;
            display: block;
            position: fixed;
            top: 0;
            width: 100%;
            background: var(--gh-bg);
            font-family: var(--gh-font);
            font-size: var(--gh-font-size);
            border-bottom: 1px solid var(--gh-border);
            box-shadow: none;
            transition: box-shadow 0.2s ease;
            z-index: var(--gh-z);
        }

        :host([hidden-bar]) {
            display: none;
        }

        :host([show-shadow][scrolled]) {
            box-shadow: 0 8px 12px 0 rgba(0, 0, 0, 0.1);
        }

        /* ── Main bar ── */

        .gh-bar {
            display: flex;
            align-items: center;
            height: var(--gh-height);
            padding: 0 24px;
            gap: 0;
        }

        /* ── Logo ── */

        .gh-logo {
            display: flex;
            align-items: center;
            flex-shrink: 0;
            margin-right: 32px;
            text-decoration: none;
        }

        .gh-logo svg,
        .gh-logo img {
            display: block;
            height: 22px;
            width: auto;
        }

        /* ── Desktop nav — fills the center section ── */

        .gh-nav {
            display: flex;
            align-items: stretch;
            flex: 1;
            height: 100%;
            overflow: hidden;
        }

        .gh-nav-list {
            display: flex;
            align-items: stretch;
            list-style: none;
            height: 100%;
            gap: 8px;
        }

        .gh-nav-item {
            display: flex;
            align-items: stretch;
        }

        /* ── Nav link — full height column: pill area + active bar ── */

        .gh-nav-link {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            height: 100%;
            color: var(--gh-text);
            text-decoration: none;
            white-space: nowrap;
            outline: none;
        }

        /* ── Text wrap — vertically centers the pill ── */

        .gh-nav-text-wrap {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
        }

        /* ── Pill — the clickable label ── */

        .gh-nav-label {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 40px;
            padding: 0 16px;
            border-radius: 99px;
            font-family: var(--gh-font);
            font-size: 16px;
            font-weight: 400;
            letter-spacing: 0.32px;
            line-height: 20px;
            color: inherit;
            transition: background-color var(--gh-transition);
        }

        /* ── Active indicator bar — sits at the very bottom of the link ── */

        .gh-nav-active-bar {
            display: block;
            width: 100%;
            height: 2px;
            border-radius: 99px 99px 0 0;
            background: transparent;
            flex-shrink: 0;
        }

        /* Hover: subtle grey pill */

        .gh-nav-link:hover .gh-nav-label,
        .gh-nav-link:focus-visible .gh-nav-label {
            background-color: rgba(0, 0, 0, 0.07);
        }

        /* Focus ring */

        .gh-nav-link:focus-visible {
            outline: 2px solid var(--gh-active-text);
            outline-offset: -2px;
            border-radius: 4px;
        }

        /* ── Active / selected state ── */

        .gh-nav-link.active {
            color: var(--gh-active-text);
        }

        .gh-nav-link.active .gh-nav-active-bar {
            background: var(--gh-active-bar);
        }

        /* Active hover: blue-tinted pill */

        .gh-nav-link.active:hover .gh-nav-label,
        .gh-nav-link.active:focus-visible .gh-nav-label {
            background-color: rgba(47, 107, 255, 0.08);
        }

        /* ── Vertical divider between primary and secondary nav groups ── */

        .gh-nav-divider {
            width: 1px;
            height: 24px;
            background: var(--gh-border);
            margin: 0 16px;
            flex-shrink: 0;
            align-self: center;
        }

        /* ── Right-side controls wrapper ── */

        .gh-right {
            display: flex;
            align-items: center;
            gap: 24px;
            flex-shrink: 0;
            margin-left: auto;
            height: 100%;
        }

        /* ── Icon group: info + member care [+ divider + bell + messages] ── */

        .gh-icon-group {
            display: flex;
            align-items: center;
            gap: 16px;
            height: 28px;
            flex-shrink: 0;
        }

        /* ── Vertical divider inside icon group ── */

        .gh-icon-divider {
            display: block;
            width: 1px;
            height: 24px;
            background: var(--gh-border);
            flex-shrink: 0;
        }

        /* ── Icon button links (info, member care, bell, messages) ── */

        .gh-icon-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            border-radius: 50%;
            text-decoration: none;
            color: var(--gh-text);
            transition: background-color var(--gh-transition);
            flex-shrink: 0;
            padding: 5px;
        }

        .gh-icon-btn:hover,
        .gh-icon-btn:focus-visible {
            background-color: rgba(0, 0, 0, 0.06);
            outline: none;
        }

        .gh-icon-btn:focus-visible {
            outline: 2px solid var(--gh-accent);
            outline-offset: 2px;
        }

        .gh-icon-btn.active {
            background-color: rgba(47, 107, 255, 0.08);
            border-radius: 50%;
        }

        .gh-icon-btn svg {
            display: block;
            width: 20px;
            height: 20px;
            pointer-events: none;
            fill: #232529;
        }

        .gh-icon-btn.active svg {
            fill: var(--gh-active-text);
        }

        /* ── Icon tooltip (desktop only) ── */

        .gh-icon-wrap {
            position: relative;
            display: flex;
            align-items: center;
        }

        .gh-icon-tooltip {
            position: absolute;
            top: calc(100% + 10px);
            left: 50%;
            transform: translateX(-50%);
            background: #232529;
            color: #ffffff;
            font-size: 12px;
            font-weight: 500;
            line-height: 1;
            padding: 6px 10px;
            border-radius: 6px;
            white-space: nowrap;
            pointer-events: none;
            opacity: 0;
            transition: opacity 0.15s ease;
            z-index: calc(var(--gh-z) + 2);
        }

        .gh-icon-tooltip::before {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-bottom-color: #232529;
        }

        .gh-icon-wrap:hover .gh-icon-tooltip {
            opacity: 1;
        }

        /* ── Avatar wrapper + button ── */

        .gh-avatar-wrap {
            position: relative;
            display: flex;
            align-items: center;
            flex-shrink: 0;
        }

        .gh-avatar-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            border-radius: 50%;
            flex-shrink: 0;
            outline: none;
        }

        .gh-avatar-btn:focus-visible {
            outline: 2px solid var(--gh-accent);
            outline-offset: 2px;
        }

        /* ── Avatar ── */

        .gh-avatar {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            object-fit: cover;
            border: 1.5px solid var(--gh-border);
            flex-shrink: 0;
        }

        .gh-avatar-initials {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: var(--gh-accent);
            color: #ffffff;
            font-size: 13px;
            font-weight: 700;
            letter-spacing: 0.5px;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            cursor: default;
            user-select: none;
        }

        .gh-avatar-placeholder {
            width: 36px;
            height: 36px;
            border-radius: 50%;
            background: #e8e8e8;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }

        /* ── Avatar dropdown panel ── */

        .gh-avatar-dropdown {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;
            width: 193px;
            background: #ffffff;
            border-radius: 8px;
            box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.06),
            0px 4px 8px 0px rgba(0, 0, 0, 0.08),
            0px 2px 6px 0px rgba(0, 0, 0, 0.06);
            z-index: calc(var(--gh-z) + 1);
            overflow: hidden;
        }

        .gh-dropdown-group {
            padding: 4px;
        }

        .gh-dropdown-profile {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 8px;
        }

        .gh-dropdown-name {
            font-size: 14px;
            font-weight: 500;
            color: #232529;
            line-height: 16px;
            letter-spacing: 0.035px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .gh-dropdown-role {
            font-size: 12px;
            font-weight: 400;
            color: #6f757e;
            line-height: 14px;
            letter-spacing: 0.12px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .gh-dropdown-divider {
            height: 1px;
            background: var(--gh-border);
        }

        .gh-dropdown-item {
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100%;
            padding: 8px;
            background: #ffffff;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            text-align: left;
            font-family: var(--gh-font);
            transition: background-color var(--gh-transition);
        }

        .gh-dropdown-item:hover {
            background-color: rgba(0, 0, 0, 0.04);
        }

        .gh-dropdown-item:focus-visible {
            outline: 2px solid var(--gh-accent);
            outline-offset: -2px;
        }

        .gh-dropdown-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 16px;
            flex-shrink: 0;
            color: #232529;
        }

        .gh-dropdown-icon svg {
            display: block;
            width: 14px;
            height: 14px;
        }

        .gh-dropdown-item-text {
            font-size: 14px;
            font-weight: 400;
            color: #232529;
            line-height: 16px;
            letter-spacing: 0.035px;
            white-space: nowrap;
        }

        /* ── Loading spinner ── */

        .gh-spinner {
            width: 14px;
            height: 14px;
            border: 2px solid rgba(0, 0, 0, 0.12);
            border-top-color: var(--gh-text-muted);
            border-radius: 50%;
            animation: gh-spin 0.7s linear infinite;
        }

        @keyframes gh-spin {
            to {
                transform: rotate(360deg);
            }
        }

        /* ── Badge wrapper — positioning context for badges ── */

        .gh-badge-wrap {
            position: relative;
        }

        /* ── Count badge (messages) ── */

        .gh-badge {
            position: absolute;
            top: -3px;
            left: 12px;
            min-width: 13px;
            height: 13px;
            padding: 0 2px;
            border-radius: 6px;
            background: #f63333;
            color: #ffffff;
            font-size: 9px;
            font-weight: 500;
            line-height: 12px;
            text-align: center;
            white-space: nowrap;
            pointer-events: none;
            z-index: 1;
        }

        /* ── Notification dot (bell — always visible, no count) ── */

        .gh-notif-dot {
            position: absolute;
            top: -3px;
            right: 2px;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #f63333;
            pointer-events: none;
            z-index: 1;
        }

        /* ─────────────────────────────────────────────
           Mobile-specific header elements
           (hidden by default — shown only below 768px)
           ───────────────────────────────────────────── */

        .gh-mobile-left {
            display: none;
            align-items: center;
            gap: 6px;
            flex: 1;
            min-width: 0;
        }

        .gh-mobile-triangle {
            display: flex;
            align-items: center;
            flex-shrink: 0;
        }

        .gh-mobile-triangle svg {
            display: block;
            width: 25px;
            height: 22px;
        }

        .gh-mobile-title {
            font-family: var(--gh-font);
            font-size: 16px;
            font-weight: 500;
            color: #000000;
            letter-spacing: 0.32px;
            line-height: 18px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .gh-mobile-right {
            display: none;
            align-items: center;
            gap: 16px;
            flex-shrink: 0;
        }

        /* ── Mobile icon button (search, member care, hamburger) ── */

        .gh-mobile-icon-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            height: 24px;
            background: none;
            border: none;
            padding: 2px;
            cursor: pointer;
            border-radius: 50%;
            text-decoration: none;
            color: var(--gh-text);
            transition: background-color var(--gh-transition);
            flex-shrink: 0;
        }

        .gh-mobile-icon-btn:hover {
            background-color: rgba(0, 0, 0, 0.06);
        }

        .gh-mobile-icon-btn.active {
            background-color: rgba(47, 107, 255, 0.08);
            border-radius: 50%;
        }

        .gh-mobile-icon-btn svg {
            display: block;
            width: 20px;
            height: 20px;
            pointer-events: none;
            fill: #232529;
        }

        .gh-mobile-icon-btn.active svg {
            fill: var(--gh-active-text);
        }

        /* ── Hamburger button (inside gh-mobile-right) ── */

        .gh-hamburger {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            width: 24px;
            height: 24px;
            background: none;
            border: none;
            cursor: pointer;
            padding: 4px 2px;
            flex-shrink: 0;
        }

        .gh-hamburger-line {
            display: block;
            width: 100%;
            height: 2px;
            background: var(--gh-text);
            border-radius: 2px;
            transition: transform var(--gh-transition), opacity var(--gh-transition);
        }

        .gh-hamburger[aria-expanded="true"] .gh-hamburger-line:nth-child(1) {
            transform: translateY(6px) rotate(45deg);
        }

        .gh-hamburger[aria-expanded="true"] .gh-hamburger-line:nth-child(2) {
            opacity: 0;
        }

        .gh-hamburger[aria-expanded="true"] .gh-hamburger-line:nth-child(3) {
            transform: translateY(-6px) rotate(-45deg);
        }

        /* ─────────────────────────────────────────────
           Mobile tray backdrop
           ───────────────────────────────────────────── */

        .gh-tray-backdrop {
            display: none;
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.4);
            z-index: calc(var(--gh-z) + 1);
        }

        .gh-tray-backdrop.open {
            display: block;
        }

        /* ─────────────────────────────────────────────
           Mobile tray — slides in from the right
           ───────────────────────────────────────────── */

        .gh-tray {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            width: 320px;
            max-width: 100vw;
            background: var(--gh-bg);
            z-index: calc(var(--gh-z) + 2);
            transform: translateX(100%);
            transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
        }

        .gh-tray.open {
            transform: translateX(0);
        }

        /* ── Tray header bar ── */

        .gh-tray-header {
            display: flex;
            align-items: center;
            justify-content: right;
            height: 48px;
            padding: 0 8px 0 16px;
            flex-shrink: 0;
        }

        .gh-tray-logo {
            display: flex;
            align-items: center;
            gap: 6px;
        }

        .gh-tray-logo svg {
            display: block;
            width: 25px;
            height: 22px;
        }

        .gh-tray-logo-text {
            font-family: var(--gh-font);
            font-size: 16px;
            font-weight: 500;
            color: #000000;
            letter-spacing: 0.32px;
            line-height: 18px;
        }

        .gh-tray-close {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            background: none;
            border: none;
            cursor: pointer;
            border-radius: 50%;
            color: var(--gh-text);
            transition: background-color var(--gh-transition);
            flex-shrink: 0;
            padding: 0;
        }

        .gh-tray-close:hover {
            background-color: rgba(0, 0, 0, 0.06);
        }

        .gh-tray-close svg {
            display: block;
            width: 20px;
            height: 20px;
        }

        /* ── Tray spacer — pushes icon/profile section toward bottom ── */

        .gh-tray-spacer {
            flex: 1;
            min-height: 32px;
        }

        /* ── Tray icon links (Resources, Notification, Messages) ── */

        .gh-tray-icon-links {
            list-style: none;
            padding: 4px 8px;
            flex-shrink: 0;
        }

        .gh-tray-icon-link-item a {
            padding: 0 16px;
        }

        .gh-tray-icon-link {
            display: flex;
            align-items: center;
            gap: 12px;
            height: 44px;
            border-radius: 8px;
            text-decoration: none;
            color: var(--gh-text);
            font-family: var(--gh-font);
            font-size: 15px;
            font-weight: 400;
            transition: background-color var(--gh-transition);
        }

        .gh-tray-icon-link:hover {
            background-color: rgba(0, 0, 0, 0.05);
        }

        .gh-tray-icon-link.active {
            color: var(--gh-active-text);
            background-color: rgba(47, 107, 255, 0.06);
            border-radius: 32px;
        }

        .gh-tray-icon-link.active:hover {
            background-color: rgba(47, 107, 255, 0.1);
        }

        .gh-tray-icon-link.active .gh-tray-icon-link-icon {
            color: var(--gh-active-text);
        }

        .gh-tray-icon-link-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            flex-shrink: 0;
            color: var(--gh-text-muted);
        }

        .gh-tray-icon-link-icon svg {
            display: block;
            width: 16px;
            height: 16px;
            fill: #232529;
        }

        .gh-tray-icon-link.active .gh-tray-icon-link-icon svg {
            fill: var(--gh-active-text);
        }

        .gh-tray-icon-link-text {
            flex: 1;
        }

        /* ── Inline badge — count shown at the right of a tray row ── */

        .gh-tray-inline-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 18px;
            height: 18px;
            padding: 0 5px;
            border-radius: 9px;
            background: #f63333;
            color: #ffffff;
            font-size: 12px;
            font-weight: 500;
            line-height: 1;
            white-space: nowrap;
            flex-shrink: 0;
        }

        /* ── Tray notification dot (inline, right-aligned) ── */

        .gh-tray-notif-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: #f63333;
            flex-shrink: 0;
            margin-left: auto;
        }

        /* ── Tray profile section ── */

        .gh-tray-profile {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 20px;
            flex-shrink: 0;
        }

        .gh-tray-profile-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            min-width: 0;
        }

        .gh-tray-profile-name {
            font-size: 15px;
            font-weight: 500;
            color: var(--gh-text);
            line-height: 20px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .gh-tray-profile-role {
            font-size: 12px;
            font-weight: 400;
            color: var(--gh-text-muted);
            line-height: 16px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        /* ── Tray nav list ── */

        .gh-tray-nav {
            list-style: none;
            padding: 8px 16px;
            flex-shrink: 0;
        }

        .gh-tray-nav-item {}

        .gh-tray-nav-link {
            display: flex;
            align-items: center;
            height: 48px;
            padding: 0 12px;
            border-radius: 8px;
            text-decoration: none;
            color: var(--gh-text);
            font-family: var(--gh-font);
            font-size: 16px;
            font-weight: 400;
            letter-spacing: 0.32px;
            transition: background-color var(--gh-transition);
        }

        .gh-tray-nav-link:hover {
            background-color: rgba(0, 0, 0, 0.05);
        }

        .gh-tray-nav-link:focus-visible {
            outline: 2px solid var(--gh-active-text);
            outline-offset: -2px;
        }

        .gh-tray-nav-link.active {
            color: var(--gh-active-text);
            background-color: rgba(47, 107, 255, 0.06);
            border-radius: 32px;
        }

        .gh-tray-nav-link.active:hover {
            background-color: rgba(47, 107, 255, 0.1);
        }

        /* ── Tray section divider ── */

        .gh-tray-divider {
            height: 1px;
            background: var(--gh-border);
            margin: 4px 0;
            flex-shrink: 0;
        }

        /* ── Tray account links ── */

        .gh-tray-account {
            list-style: none;
            padding: 8px 0;
            flex-shrink: 0;
        }

        .gh-tray-account-item {
            padding: 0 8px;
        }

        .gh-tray-account-btn {
            display: flex;
            align-items: center;
            gap: 10px;
            width: 100%;
            height: 44px;
            padding: 0 12px;
            background: none;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            text-align: left;
            font-family: var(--gh-font);
            transition: background-color var(--gh-transition);
        }

        .gh-tray-account-btn:hover {
            background-color: rgba(0, 0, 0, 0.04);
        }

        .gh-tray-account-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            flex-shrink: 0;
            color: var(--gh-text-muted);
        }

        .gh-tray-account-icon svg {
            display: block;
            width: 16px;
            height: 16px;
        }

        .gh-tray-account-icon.logout-icon {
            color: #e53e3e;
        }

        .gh-tray-account-text {
            font-size: 15px;
            font-weight: 400;
            color: var(--gh-text);
            line-height: 20px;
            white-space: nowrap;
        }

        .gh-tray-account-text.logout-text {
            color: #e53e3e;
        }

        /* ─────────────────────────────────────────────
           Tablet breakpoint: 768px – 1024px
           Tighten spacing, shrink nav labels and icons.
           Desktop layout is preserved; only sizing changes.
           ───────────────────────────────────────────── */

        @media (min-width: 768px) and (max-width: 1024px) {
            :host {
                --gh-height: 64px;
            }

            .gh-bar {
                padding: 0 16px;
            }

            .gh-logo {
                margin-right: 20px;
            }

            .gh-nav-list {
                gap: 0;
            }

            .gh-nav-label {
                font-size: 14px;
                height: 36px;
                padding: 0 12px;
                letter-spacing: 0.28px;
                line-height: 16px;
            }

            .gh-nav-divider {
                margin: 0 8px;
            }

            .gh-right {
                gap: 20px;
            }

            .gh-icon-group {
                gap: 20px;
                height: 24px;
            }

            .gh-icon-btn {
                width: 19.2px;
                height: 19.2px;
                padding: 1.6px;
            }

            .gh-notif-dot {
                right: -3px;
            }

            .gh-icon-btn svg {
                width: 16px;
                height: 16px;
            }

            .gh-avatar {
                width: 32px;
                height: 32px;
            }

            .gh-avatar-initials {
                width: 32px;
                height: 32px;
                font-size: 12px;
            }

            .gh-avatar-placeholder {
                width: 32px;
                height: 32px;
            }
        }

        /* ─────────────────────────────────────────────
           Mobile breakpoint: below 768px
           Swap to triangle + title layout with right-side
           icon strip (search [conditional], member care, hamburger).
           ───────────────────────────────────────────── */

        @media (max-width: 767px) {
            :host {
                --gh-height: 48px;
            }

            .gh-bar {
                padding: 0 16px;
            }

            /* Hide desktop/tablet elements */
            .gh-logo {
                display: none;
            }

            .gh-nav {
                display: none;
            }

            .gh-right {
                display: none;
            }

            /* Show mobile-specific elements */
            .gh-mobile-left {
                display: flex;
            }

            .gh-mobile-right {
                display: flex;
            }
        }`
